import { isMobilePublishEnvironment } from '../rtc/media/publish-fps'
import { DEFAULT_CAMERA_REALTIME_SETTINGS } from './default'
import { validateRealtimeStreamSetting } from './utils'
import type {
  RealtimeConnectCameraOptions,
  RealtimeMediaSetting,
} from './types'

/**
 * @brief 获取当前环境使用的摄像头默认媒体设置。
 * @param mobile 是否使用移动端默认值；省略时由 SDK 根据当前视口自动判断。
 * @returns 一份可独立修改的完整媒体设置。
 */
export function resolveDefaultCameraRealtimeSetting(
  mobile = isMobilePublishEnvironment(),
): RealtimeMediaSetting {
  return {
    ...(mobile
      ? DEFAULT_CAMERA_REALTIME_SETTINGS.mobile
      : DEFAULT_CAMERA_REALTIME_SETTINGS.desktop),
  }
}

/**
 * @brief 将面向调用方的摄像头发送尺寸转换为火山 RTC 编码尺寸。
 * 火山 RTC 在移动端竖持状态下会将设置的宽高反转后发流，因此 SDK
 * 在移动端预先交换一次，确保实际发送尺寸与调用方配置保持一致。
 * @param setting 调用方期望的实际发送规格。
 * @param mobile 是否为移动端竖持场景。
 * @returns 传给火山 RTC `setVideoEncoderConfig` 的宽高。
 * @internal
 */
export function resolveCameraRtcEncoderSize(
  setting: Pick<RealtimeMediaSetting, 'width' | 'height'>,
  mobile = isMobilePublishEnvironment(),
): [number, number] {
  return mobile
    ? [setting.height, setting.width]
    : [setting.width, setting.height]
}

/**
 * @brief 使用回退值补齐部分媒体设置。
 * @param setting 调用方传入的可选媒体字段。
 * @param fallback 缺失字段使用的完整回退设置。
 * @returns 补齐后的完整媒体设置。
 */
function fillRealtimeMediaSetting(
  setting: Partial<RealtimeMediaSetting> | undefined,
  fallback: RealtimeMediaSetting,
): RealtimeMediaSetting {
  return {
    width: setting?.width ?? fallback.width,
    height: setting?.height ?? fallback.height,
    fps: setting?.fps ?? fallback.fps,
    maxKbps: setting?.maxKbps ?? fallback.maxKbps,
    contentHint: setting?.contentHint ?? fallback.contentHint,
  }
}

/**
 * @brief 将摄像头连接的可选编码参数归一化。
 * @param options connectCamera 中的 stream 配置。
 * @param mobile 是否使用移动端默认值；省略时由 SDK 根据当前视口自动判断。
 * @returns 补齐后的完整 RTC 编码设置。
 * @internal
 */
export function resolveCameraRealtimeStreamSetting(
  options: Pick<RealtimeConnectCameraOptions, 'stream'>,
  mobile = isMobilePublishEnvironment(),
): RealtimeMediaSetting {
  validateRealtimeStreamSetting(options.stream)

  const defaults = resolveDefaultCameraRealtimeSetting(mobile)
  return fillRealtimeMediaSetting(options.stream, defaults)
}
